import React, { useEffect } from 'react';
import { Switch, Route, Redirect } from 'react-router-dom';
import { object, bool } from 'prop-types';
import { logger } from '@fiverr-private/obs';
import { CMSContextProvider } from '@fiverr-private/cms_ui';
import Header from '../Header';
import { Job, GeneralPage } from '../pages';
import { LINKS, PAGE_NAMES } from '../constants';
import withTranslation from '../HOCs/withTranslation';
import { initComeet, loadComeetAssets } from '../../utils/dataFunctions';
import EventsReporter from '../../utils/events/reporter';
import {
    trackPageEvent,
    GTM_CUSTOM_EVENTS_TEAMS,
    GTM_CUSTOM_EVENT_CAREERS_PAGE,
    GTM_CUSTOM_EVENT_THANKYOU_PAGE,
} from '../../utils/events/gtm';
import { getTeams } from '../../utils';
import { PAGE_NAME } from '../../server/constants';
import '../styles.scss';

const App = (props) => {
    const { data, isBlockedCountry } = props;
    const dataToPass = { ...props };
    const teams = getTeams();

    useSyncCanonicalUrl();

    useEffect(() => {
        loadComeetAssets().then(initComeet).catch(logger.error);
    }, []);

    useEffect(() => {
        EventsReporter.enrich({});
        trackPageEvent(GTM_CUSTOM_EVENT_CAREERS_PAGE);
    }, []);

    if (!data) {
        return null;
    }

    return (
        <CMSContextProvider value={{ pageInfo: { pageName: PAGE_NAME } }}>
            <div className={isBlockedCountry ? 'blocked-country' : null}>
                <Header teams={teams} />

                <Switch>
                    <Route
                        path={`/${LINKS.LIFE_AT_FIVERR}`}
                        key="life-at-fiverr"
                        component={() => (
                            <GeneralPage
                                {...dataToPass}
                                {...data[LINKS.LIFE_AT_FIVERR]}
                                pageName={PAGE_NAMES.LIFE_AT_FIVERR}
                            />
                        )}
                    />
                    <Route
                        exact
                        path={`/${LINKS.TEAMS}`}
                        key="all-positions"
                        component={() => (
                            <GeneralPage {...dataToPass} {...data[LINKS.TEAMS]} pageName={PAGE_NAMES.TEAMS} />
                        )}
                    />
                    <Route
                        path={`/${LINKS.TEAMS}/:teamId`}
                        key="team"
                        component={({
                            match: {
                                params: { teamId },
                            },
                        }) => (
                            <GeneralPage
                                {...dataToPass}
                                {...data[`${LINKS.TEAMS}-${teamId.replace(/_/g, '-')}`]}
                                teamId={teamId}
                                pageName={PAGE_NAMES.TEAM}
                                gtm={GTM_CUSTOM_EVENTS_TEAMS[teamId]}
                            />
                        )}
                    />
                    <Route
                        path="/offices/:teamId"
                        key="redirect-offices"
                        component={({
                            match: {
                                params: { teamId },
                            },
                        }) => <Redirect to={`/${LINKS.TEAMS}?location=${teamId}`} />}
                    />
                    <Route
                        path={`/${LINKS.APPLIED}`}
                        key="applied"
                        component={() => (
                            <GeneralPage
                                {...dataToPass}
                                {...data[LINKS.APPLIED]}
                                pageName={PAGE_NAMES.APPLIED}
                                pageClasses="thankyou"
                                gtm={GTM_CUSTOM_EVENT_THANKYOU_PAGE}
                            />
                        )}
                    />
                    <Route
                        exact
                        path={'/:jobId'}
                        key="position"
                        component={({ match }) => <Job match={match} {...dataToPass} pageName={PAGE_NAMES.JOBS} />}
                    />
                    <Route
                        exact
                        path="/"
                        key="home"
                        render={() => <GeneralPage {...dataToPass} {...data.jobs} pageName={PAGE_NAMES.HOME} />}
                    />
                    <Route path="/" key="redirect" component={() => <Redirect to="/" />} />
                </Switch>
            </div>
        </CMSContextProvider>
    );
};

// Note - using this as a replacement for `react-router-dom`'s `useLocation` hook which is only available since React v16.8 (we're using v16.4)
function useSyncCanonicalUrl() {
    const PUSH_STATE_EVENT_NAME = 'pushstate';

    const handlePushState = React.useCallback(() => {
        const canonicalLink = document.querySelector('link[rel="canonical"]');
        if (canonicalLink) {
            canonicalLink.setAttribute('href', window.location.href);
        }
    }, []);

    React.useEffect(() => {
        const _pushState = window.history.pushState;
        window.history.pushState = function () {
            _pushState.apply(window.history, arguments);
            window.dispatchEvent(new Event(PUSH_STATE_EVENT_NAME));
        };

        window.addEventListener(PUSH_STATE_EVENT_NAME, handlePushState);

        return () => {
            window.history.pushState = _pushState;
            window.removeEventListener(PUSH_STATE_EVENT_NAME, handlePushState);
        };
    }, [handlePushState]);
}

App.propTypes = {
    data: object,
    isBlockedCountry: bool,
};
export default withTranslation(App);
